"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState, type ReactNode } from "react";
import { Train, ArrowRight, User, LogOut, ChevronDown, Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { getStoredCandidate, clearCandidateSession } from "@/lib/auth";
import type { Candidate } from "@/lib/applicant";
import { useLanguage } from "@/lib/i18n/LanguageContext";

export function LanguageSwitcher() {
  const { lang, setLang } = useLanguage();

  return (
    <div className="flex items-center rounded-lg border border-border/70 bg-muted/60 p-0.5 text-xs font-semibold shadow-xs">
      <button
        type="button"
        onClick={() => setLang("en")}
        className={`flex items-center gap-1 rounded-md px-2.5 py-1 transition-all ${lang === "en"
          ? "bg-background text-navy shadow-xs font-bold"
          : "text-muted-foreground hover:text-foreground"
          }`}
        aria-label="Switch to English"
      >
        <span>English</span>
      </button>
      <button
        type="button"
        onClick={() => setLang("mr")}
        className={`flex items-center gap-1 rounded-md px-2.5 py-1 transition-all ${lang === "mr"
          ? "bg-background text-navy shadow-xs font-bold"
          : "text-muted-foreground hover:text-foreground"
          }`}
        aria-label="मराठीत बदला"
      >
        <span>मराठी</span>
      </button>
    </div>
  );
}

export function PortalHeader() {
  const router = useRouter();
  const { t } = useLanguage();
  const [candidate, setCandidate] = useState<Candidate | null | undefined>(undefined);

  useEffect(() => {
    setCandidate(getStoredCandidate());
  }, []);

  function handleLogout() {
    clearCandidateSession();
    setCandidate(null);
    router.push("/login");
  }

  return (
    <header className="sticky top-0 z-40 border-b border-border/70 bg-background/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* <Link href="/" className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--gradient-hero)] shadow-[var(--shadow-soft)]">
            <Train className="h-5 w-5 text-white" aria-hidden />
          </div>
          <div className="min-w-0">
            <div className="font-display text-[15px] font-bold leading-tight tracking-tight text-navy">MMMOCL</div>
            <div className="text-[11px] font-medium leading-tight text-muted-foreground">{t.header.brandSubtitle}</div>
          </div>
        </Link> */}
        {/* Logo and title */}
        <a href="http://103.234.116.51/mumbai-metro/" className="flex items-center gap-3">
          <img src="/images/new-logo (3).png" alt="Mumbai Metro Logo" className="h-12 w-auto" />
          <div className="flex flex-col leading-tight">
            <span className="font-display text-sm font-bold">Maha Mumbai Metro</span>
            <span className="text-[9px] uppercase tracking-[0.2em] text-black/60">
              Operation Corporation Ltd.
            </span>
          </div>
        </a>
        <nav aria-label="Primary" className="hidden items-center gap-7 text-sm font-medium text-muted-foreground md:flex">
          <Link href="/#openings" className="hover:text-foreground">{t.header.openings}</Link>
          <Link href="/#how" className="hover:text-foreground">{t.header.howToApply}</Link>
          <Link href="/#why" className="hover:text-foreground">{t.header.lifeAtMMMOCL}</Link>
          <a href="#" className="hover:text-foreground">{t.header.notices}</a>
          <a href="#" className="hover:text-foreground">{t.header.help}</a>
        </nav>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          {candidate === undefined ? (
            <div className="h-9 w-[104px]" aria-hidden />
          ) : candidate ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-navy outline-none hover:bg-muted">
                <span className="grid h-7 w-7 place-items-center rounded-full bg-primary/10 text-primary">
                  <User className="h-4 w-4" />
                </span>
                <span className="hidden max-w-[8rem] truncate sm:inline">{candidate.full_name.split(" ")[0]}</span>
                <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="truncate font-normal text-muted-foreground">
                  {t.header.signedInAs} <span className="font-semibold text-navy">{candidate.full_name}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link href="/profile" className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" /> {t.header.myProfile}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" /> {t.header.logout}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link href="/login" className="hidden rounded-lg px-3 py-2 text-sm font-semibold text-navy hover:bg-muted sm:inline-flex">
                {t.header.signIn}
              </Link>
              <Link href="/register" className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-soft)] transition hover:brightness-110">
                {t.header.register} <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export function PortalFooter() {
  const { t } = useLanguage();

  return (
    <footer className="border-t border-border bg-navy text-white/80">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-white/10">
              <Train className="h-5 w-5 text-white" aria-hidden />
            </div>
            <div>
              <div className="font-display text-sm font-bold text-white">{t.footer.title}</div>
              <div className="text-xs text-white/60">© {new Date().getFullYear()} {t.footer.copyright}</div>
            </div>
          </div>
          <nav aria-label="Footer" className="flex flex-wrap gap-x-6 gap-y-2 text-xs font-medium text-white/70">
            <a href="#" className="hover:text-white">{t.footer.privacy}</a>
            <a href="#" className="hover:text-white">{t.footer.terms}</a>
            <a href="#" className="hover:text-white">{t.footer.accessibility}</a>
            <a href="#" className="hover:text-white">{t.footer.rti}</a>
            <a href="#" className="hover:text-white">{t.footer.contact}</a>
          </nav>
        </div>
      </div>
    </footer>
  );
}

export function PortalShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col bg-background font-sans text-foreground">
      <PortalHeader />
      <main className="flex-1">{children}</main>
      <PortalFooter />
    </div>
  );
}